/**
 * VU customizer — boots the VU customizer inline on a page.
 *
 * VU is loaded by this element rather than by a static tag so it only boots once
 * the markup it mounts into (#customizer-root) is in the DOM. Rumpl's customizer
 * glue (assets/vu-customizer.js) reads the rendered scene, so it is injected on
 * VU's `customizer.scene-rendered` event, as on the live PDP.
 *
 * The customizer builds its controls over several seconds, so the element carries
 * `data-loading` (from the markup) until the glue has run, and the CSS shows a
 * loader in place of the half-built customizer.
 * @extends HTMLElement
 */
const VU_SCRIPT_SRC = 'https://vu-customizer.s3.amazonaws.com/production/vu_customizer.js';
const GLUE_SETTLE_MS = 500;

if (!customElements.get('vu-customizer')) {
  class VuCustomizer extends HTMLElement {
    constructor() {
      super();
      this.onSceneRendered = this.onSceneRendered.bind(this);
      this.onQuantityClick = this.onQuantityClick.bind(this);
    }

    connectedCallback() {
      if (!this.root || !this.dataset.variantId) return;

      this.setGlobals();
      this.quantityContainer?.addEventListener('click', this.onQuantityClick);
      document.addEventListener('customizer.scene-rendered', this.onSceneRendered, { once: true });
      this.loadVu();
    }

    disconnectedCallback() {
      this.quantityContainer?.removeEventListener('click', this.onQuantityClick);
      document.removeEventListener('customizer.scene-rendered', this.onSceneRendered);
    }

    get root() {
      return this.querySelector('#customizer-root');
    }

    get quantityContainer() {
      return this.querySelector('.customizer-quantity-selector-container');
    }

    get quantityInput() {
      return this.querySelector('#customizer-quantity-input');
    }

    /** Globals VU and the glue read: the SKU stock map and the add-to-cart quantity. */
    setGlobals() {
      const quantity = parseInt(this.quantityInput?.value, 10) || 1;

      window.vuCustomizer = window.vuCustomizer || {};
      window.vuCustomizer.skus = this.readSkus();
      window.vuCustomizer.quantity = quantity;
      window.vuCustomizer.getQuantity = () => window.vuCustomizerQuantity || 1;
      window.vuCustomizerQuantity = quantity;

      // Rumpl's VU template script calls the live theme's refreshCart() once its own
      // /cart/add.js succeeds; without it VU reports the add as failed. VU adds outside
      // the theme cart engine (assets/cart.js), so re-sync that and open the drawer.
      window.refreshCart = window.refreshCart || (() =>
        window.Cart?.refresh()
          .then(() => document.querySelector('cart-drawer')?.open())
          .catch((error) => console.error('VU cart refresh:', error)));
    }

    readSkus() {
      try {
        return JSON.parse(this.querySelector('[data-customizer-skus]')?.textContent || '{}');
      } catch (error) {
        console.error('VU customizer SKU map:', error);
        return {};
      }
    }

    loadVu() {
      if (document.getElementById('customizer')) return;

      const script = document.createElement('script');
      script.id = 'customizer';
      script.src = VU_SCRIPT_SRC;
      script.dataset.variant = this.dataset.variantId;
      script.addEventListener('error', () => this.setReady());
      document.body.appendChild(script);
    }

    onSceneRendered() {
      if (document.querySelector('script[data-vu-glue]')) return;

      const script = document.createElement('script');
      script.src = this.dataset.glueSrc;
      script.dataset.vuGlue = '';
      // `load` fires after the glue has executed; its initInlineCustomizer() then
      // restores saved state and picks the default font on timers up to 500ms,
      // so hold the loader until those have applied.
      script.addEventListener('load', () => setTimeout(() => this.setReady(), GLUE_SETTLE_MS));
      script.addEventListener('error', () => this.setReady());
      document.body.appendChild(script);
    }

    setReady() {
      delete this.dataset.loading;
      this.removeAttribute('aria-busy');
    }

    /** The glue listens for `change` on the input to sync the add-to-cart quantity. */
    onQuantityClick(event) {
      const button = event.target.closest('.quantity__button');
      const input = this.quantityInput;
      if (!button || !input) return;

      const current = parseInt(input.value, 10) || 1;
      const min = parseInt(input.min, 10) || 1;
      const next = button.name === 'plus' ? current + 1 : Math.max(min, current - 1);
      if (next === current) return;

      input.value = next;
      input.dispatchEvent(new Event('change', { bubbles: true }));
    }
  }

  customElements.define('vu-customizer', VuCustomizer);
}
